import { formatDate, PROFILE_VIEW_SECTION, scoreBg, scoreColor } from '@workspace/seedforge-shared'
import { VideoThumbnail } from '@workspace/seedforge-ui/components/video-thumbnail'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowRight, Lightbulb, Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { Section } from '../components/section.js'
import type { DataRoomPitchRecording } from '../types.js'

export interface PitchRecordingSectionProps {
  /**
   * Investor-facing view. An empty section is hidden rather than explaining to
   * the visitor how to fill it in — "Complete a pitch evaluation" is an
   * instruction the investor cannot act on, and it lands in the content they
   * just unlocked. Same rule as the header's missing meta.
   */
  isPublic?: boolean
  pitchRecording: DataRoomPitchRecording | undefined
  /** Video URL for the pitch recording thumbnail */
  videoUrl?: string | null
  /** Whether the video modal is currently open */
  modalOpen?: boolean
  /** Resume time for the video player */
  resumeTime?: number
  /** Called when the user clicks to play the video */
  onPlay?: (currentTime: number) => void
  /** Startup name displayed in the pitch recording card */
  startupName?: string
  /**
   * Action offered when there is no recording yet — the founder's own view
   * passes "Complete AI Session" here. Never set on the investor-facing view,
   * where the empty section is hidden entirely.
   */
  emptyAction?: ReactNode
}

export function PitchRecordingSection({
  pitchRecording,
  videoUrl,
  modalOpen,
  resumeTime,
  onPlay,
  startupName,
  isPublic,
  emptyAction,
}: PitchRecordingSectionProps) {
  if (isPublic && !pitchRecording) return null

  return (
    <Section dataSection={PROFILE_VIEW_SECTION.PITCH}>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Pitch Recording
        </h2>
      </div>
      {!pitchRecording && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground/50">
            No pitch recordings yet. Complete a pitch evaluation to see your recording here.
          </p>
          {/* The offer belongs where the gap is, not in a toolbar at the top of
              the page — this is the one place the founder is told a recording
              is missing. */}
          {emptyAction}
        </div>
      )}
      {pitchRecording && (
        <div className="space-y-4">
          {/* Video + meta */}
          <div className="flex flex-col overflow-hidden rounded-lg bg-card sm:flex-row">
            <VideoThumbnail
              videoUrl={videoUrl}
              duration={pitchRecording.duration}
              modalOpen={modalOpen}
              resumeTime={resumeTime}
              onPlay={onPlay}
              placeholder={
                <span className="absolute text-3xl font-bold text-input select-none">
                  SEEDFORGE
                </span>
              }
              className="aspect-video w-full shrink-0 sm:w-64"
            />

            <div className="flex flex-1 flex-col justify-center p-4 lg:p-5">
              <h3 className="text-sm font-semibold text-white">
                AI Session recording{startupName ? `: ${startupName}` : ''}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">Watch the full pitch recording.</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground/50">
                <span>{pitchRecording.duration} recording</span>
                <span>&middot;</span>
                <span>Evaluated {formatDate(pitchRecording.recordedDate)}</span>
              </div>
            </div>
          </div>

          {/* Metric scores */}
          {pitchRecording.metricScores && pitchRecording.metricScores.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {pitchRecording.metricScores.map((m) => (
                <div key={m.label} className="rounded-lg bg-card px-3 py-2.5">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] text-muted-foreground">{m.label}</span>
                    <span className={cn('text-sm font-bold tabular-nums', scoreColor(m.score))}>
                      {m.score}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full bg-input">
                    <div
                      className={cn('h-1 rounded-full', scoreBg(m.score))}
                      style={{ width: `${m.score}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Winning persona */}
          {pitchRecording.winningPersona && (
            <div
              className="rounded-xl border px-5 py-4"
              style={{
                borderColor: `${pitchRecording.winningPersona.color}25`,
                backgroundColor: `${pitchRecording.winningPersona.color}08`,
              }}
            >
              <div
                className="flex items-center gap-2 text-xs"
                style={{ color: pitchRecording.winningPersona.color }}
              >
                <Lightbulb className="size-3.5" />
                <span className="font-medium">Investment Thesis</span>
                <span style={{ opacity: 0.5 }}>&middot;</span>
                <span style={{ opacity: 0.6 }}>{pitchRecording.winningPersona.name}</span>
                <span
                  className="ml-1 rounded-full px-1.5 py-px text-[10px] font-medium"
                  style={{
                    backgroundColor: `${pitchRecording.winningPersona.color}15`,
                  }}
                >
                  {pitchRecording.winningPersona.verdict}
                </span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-[#ccc]">
                {pitchRecording.winningPersona.thesis}
              </p>
            </div>
          )}

          {/* CTA to full evaluation */}
          {pitchRecording.evaluationTo && (
            <a
              href={pitchRecording.evaluationTo}
              className="flex items-center justify-between rounded-xl border border-[#f97316]/20 bg-[#f97316]/[0.06] px-5 py-3.5 transition-colors hover:bg-[#f97316]/[0.1]"
            >
              <div className="flex items-center gap-3">
                <Sparkles className="size-4 text-[#f97316]" />
                <span className="text-sm font-medium text-white">View Full Evaluation</span>
              </div>
              <ArrowRight className="size-4 text-[#f97316]" />
            </a>
          )}
        </div>
      )}
    </Section>
  )
}
